/**
 * AviIncomingRequestModal — spec iter159 (Feb 2026).
 *
 * Popup d'alerte immédiate BLOQUANT qui apparaît par-dessus toute page
 * de l'app dès qu'une nouvelle demande AVI (Validation, Accusé,
 * Information) arrive pour un utilisateur qui a un rôle ACTIF sur cette
 * demande (décideur / réceptionnaire / lecteur — pas les témoins).
 *
 * File d'attente :
 *   • Trie par priorité DÉCROISSANTE : immediat > urgent > moyen > normal
 *   • Un seul popup à la fois. Le suivant s'affiche après traitement
 *     ou fermeture (« Voir plus tard ») du précédent.
 *   • Compteur "il reste N autres demandes en attente" en pied de fiche.
 *
 * Boutons de décision selon avi_type :
 *   • validation  → Approuver  · Refuser
 *   • accuse      → Accuser réception  (Refuser retiré côté backend iter145)
 *   • information → Marquer comme lu   (bouton unique)
 *
 * « Voir plus tard » = vrai bouton encadré, ferme le popup sans
 * décision (la demande reste « En attente », sera revisible dans
 * la liste AVI).
 *
 * Son : `playAviChime()` à chaque nouvelle demande destination active.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, XCircle, Ban, Clock, AlertTriangle, Loader2, X, ExternalLink } from "lucide-react";
import { toast } from "@/lib/toast";
import { useLocation } from "react-router-dom";
import { useNavigate } from "@/hooks/useDeskNavigate";
import api from "@/lib/api";
import useAviStream from "@/hooks/useAviStream";
import { useAuth } from "@/context/AuthContext";
import { playAviChime } from "@/lib/aviChime";
import { isSoundAllowed } from "@/lib/soundPrefs";
import AviDetailBody from "@/components/avi/AviDetailBody";
import { AVI_SUBTYPES } from "@/components/icons/AviSubtypeIcons";

// Ordre de priorité DÉCROISSANT — les plus critiques d'abord.
const PRIORITY_WEIGHT = {
  immediat: 4,
  urgent:   3,
  moyen:    2,
  normal:   1,
};

const PRIORITY_LABEL = {
  immediat: "Immédiat",
  urgent:   "Urgent",
  moyen:    "Moyen",
  normal:   "Normal",
};

const PRIORITY_COLOR = {
  immediat: { bg: "rgba(220,38,38,0.15)", border: "#DC2626", text: "#F87171" },
  urgent:   { bg: "rgba(234,88,12,0.15)", border: "#EA580C", text: "#FB923C" },
  moyen:    { bg: "rgba(234,179,8,0.15)", border: "#EAB308", text: "#FDE047" },
  normal:   { bg: "rgba(148,163,184,0.15)", border: "#94A3B8", text: "#CBD5E1" },
};

const SUBTYPE_META = {
  validation:  { label: "Validation",  color: "#EF4444", positive: "Approuver",         negative: "Refuser",  positiveDec: "approuvee", negativeDec: "refusee" },
  accuse:      { label: "Accusé",      color: "#F97316", positive: "Accuser réception", negative: null,       positiveDec: "accusee",   negativeDec: null },
  information: { label: "Information", color: "#2a9a7a", positive: "Marquer comme lu",  negative: null,       positiveDec: "lue",       negativeDec: null },
};

function _sortQueue(queue) {
  return [...queue].sort((a, b) => {
    const pa = PRIORITY_WEIGHT[a.priority] || 0;
    const pb = PRIORITY_WEIGHT[b.priority] || 0;
    if (pa !== pb) return pb - pa;  // décroissant
    return new Date(a.at || 0).getTime() - new Date(b.at || 0).getTime();
  });
}

export default function AviIncomingRequestModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [queue, setQueue] = useState([]);       // list of {avi_id, avi_type, priority, at}
  const [detail, setDetail] = useState(null);   // fiche complète du current
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const seenRef = useRef(new Set());            // dédup si SSE réémet
  // Spec iter160 (Feb 2026) — quand l'utilisateur clique « Ouvrir la
  // fiche complète », on mémorise l'avi ici et on ferme le popup. Un
  // useEffect surveille les changements de route pour re-vérifier
  // l'état côté serveur : si le participant courant est toujours
  // ``en_attente`` (aucune décision), la demande est ré-injectée en
  // tête de la file d'attente pour ne pas être oubliée.
  const [peekedIds, setPeekedIds] = useState(() => []);
  const peekedRef = useRef([]);
  useEffect(() => { peekedRef.current = peekedIds; }, [peekedIds]);

  // -- Handler SSE : nouvelle demande créée -------------------------------
  const push = useCallback(async (evt) => {
    if (!evt || evt.kind !== "created" || !evt.avi_id) return;
    if (!user) return;
    if (evt.actor_id && evt.actor_id === user.id) return;   // sa propre demande
    if (seenRef.current.has(evt.avi_id)) return;
    seenRef.current.add(evt.avi_id);

    // Vérifie côté client que l'utilisateur est un DESTINATAIRE ACTIF
    // (décideur / réceptionnaire / lecteur), pas un simple témoin.
    try {
      const { data: fiche } = await api.get(`/avi/${evt.avi_id}`);
      const me = (fiche?.participants || []).find((p) => p.user_id === user.id);
      if (!me) return;
      // Décision active : role != "temoin"
      const role = (me.role || "").toLowerCase();
      const isActive = role === "decideur" || role === "receptionnaire" || role === "lecteur";
      if (!isActive) return;
      // Ne pas re-pop si l'utilisateur a déjà pris sa décision (dédup fort).
      if (me.decision && me.decision !== "en_attente") return;

      // Spec iter269 — gate central Paramètres → Son des notifications.
      if (isSoundAllowed("avi_new_request")) {
        try { playAviChime({ force: true }); } catch { /* silent */ }
      }
      setQueue((q) => _sortQueue([
        ...q,
        {
          avi_id: fiche.id,
          avi_type: fiche.avi_type,
          priority: fiche.priority || "normal",
          reference: fiche.reference,
          subject: fiche.subject,
          at: evt.at,
        },
      ]));
    } catch (_err) {
      // 403 / 404 : la demande n'est plus visible pour cet utilisateur.
    }
  }, [user]);

  useAviStream({ onCreated: push }, { enabled: !!user });

  const current = queue[0];

  // Fetch la fiche complète du courant à chaque changement de tête de file.
  useEffect(() => {
    let cancelled = false;
    if (!current?.avi_id) { setDetail(null); return () => {}; }
    setLoadingDetail(true);
    setDetail(null);
    api.get(`/avi/${current.avi_id}`)
      .then(({ data }) => { if (!cancelled) setDetail(data); })
      .catch(() => {
        if (!cancelled) {
          // La demande n'est plus accessible — passe à la suivante.
          toast.info("Demande introuvable — passage à la suivante");
          setQueue((q) => q.slice(1));
        }
      })
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
  }, [current?.avi_id]);

  // ---- Actions ---------------------------------------------------------
  const closeCurrent = () => setQueue((q) => q.slice(1));

  // Spec iter160 · point 1 — la touche ÉCHAP ferme le popup avec la
  // même sémantique que « Voir plus tard » (aucune décision, la demande
  // reste en_attente). Écoute clavier globale seulement quand un popup
  // est réellement visible pour ne pas casser l'UX Escape des autres
  // modales/menus.
  useEffect(() => {
    if (!current) return () => {};
    const onKey = (e) => {
      if (e.key !== "Escape" || submitting) return;
      e.stopPropagation();
      e.preventDefault();
      closeCurrent();
    };
    // capture=true → on prend le dessus AVANT que d'autres handlers
    // interceptent Escape (ex. Dialog Radix).
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [current, submitting]);

  // Spec iter160 · point 2 — bouton « Ouvrir la fiche complète » :
  // ferme le popup + garde l'avi en surveillance (peeked). Un useEffect
  // ci-dessous, déclenché à chaque changement de route, re-vérifie
  // l'état de chaque avi peeked : si toujours en_attente pour l'user
  // courant, on ré-injecte dans la file (top-priority tri en tenant
  // compte du weight, comme les autres).
  const openFullDetail = () => {
    if (!current || submitting) return;
    const id = current.avi_id;
    setPeekedIds((p) => (p.includes(id) ? p : [...p, id]));
    // Ferme le popup en cours (retire de la file). Il sera ré-injecté
    // seulement si aucune décision n'a été prise à la fermeture de la
    // fiche complète.
    closeCurrent();
    navigate(`/avi/${id}`);
  };

  // Sur chaque changement de route (ex. l'utilisateur quitte
  // /avi?open=... pour aller ailleurs), on inspecte tous les avis
  // peeked et on ré-injecte ceux qui restent ``en_attente`` pour le
  // participant courant.
  const currentPath = location.pathname + location.search;
  useEffect(() => {
    if (!user) return () => {};
    let cancelled = false;
    const pending = peekedRef.current.slice();
    if (pending.length === 0) return () => {};
    (async () => {
      const stillPeeked = [];
      const toReinject = [];
      for (const id of pending) {
        try {
          const { data: f } = await api.get(`/avi/${id}`);
          const me = (f?.participants || []).find((p) => p.user_id === user.id);
          const dec = (me?.decision || "en_attente").toLowerCase();
          if (dec === "en_attente") {
            // Si l'utilisateur est ENCORE sur la vue fiche complète
            // pour cet avi, on garde en peeked (pas de re-pop tant qu'il
            // regarde). Sinon on ré-injecte.
            const stillOnFiche =
              location.pathname === `/avi/${id}`;
            if (stillOnFiche) {
              stillPeeked.push(id);
            } else {
              toReinject.push({
                avi_id: f.id,
                avi_type: f.avi_type,
                priority: f.priority || "normal",
                reference: f.reference,
                subject: f.subject,
                at: f.created_at,
                _reinjected: true,
              });
            }
          }
          // else : décision prise → sort du peeked (ne re-pop pas)
        } catch (_e) {
          // 404/403 : sort du peeked
        }
      }
      if (cancelled) return;
      if (toReinject.length > 0) {
        setQueue((q) => _sortQueue([...q, ...toReinject]));
        // Autorise la re-notification en réouverture (seenRef dedup).
        toReinject.forEach((r) => seenRef.current.delete(r.avi_id));
      }
      setPeekedIds(stillPeeked);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  const submitDecision = async (decision) => {
    if (!current || submitting) return;
    let endpoint;
    if (decision === "accusee") endpoint = "acknowledge";
    else if (decision === "lue") endpoint = "mark-read";
    else if (decision === "approuvee") endpoint = "approve";
    else endpoint = "refuse";
    setSubmitting(true);
    try {
      await api.post(`/avi/${current.avi_id}/${endpoint}`, { decision, reason: null, comment_type: null });
      const okMsg = decision === "approuvee" ? "Approuvée"
                  : decision === "accusee"   ? "Reçue"
                  : decision === "lue"       ? "Marquée comme lue"
                  : "Refusée";
      toast.success(okMsg);
      closeCurrent();
    } catch (e) {
      toast.error("Échec", { description: e?.response?.data?.detail || "" });
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Rendu -----------------------------------------------------------
  const meta = current ? SUBTYPE_META[current.avi_type] : null;
  const prioCol = current ? PRIORITY_COLOR[current.priority] || PRIORITY_COLOR.normal : null;
  const remaining = Math.max(0, queue.length - 1);
  // Params dérivés pour AviDetailBody (readonly-ish : boutons désactivés).
  const bodyProps = useMemo(() => {
    if (!detail || !user) return null;
    const isAccuse = detail.avi_type === "accuse";
    const isInfo = detail.avi_type === "information";
    return {
      doc: detail,
      meta: AVI_SUBTYPES[detail.avi_type],
      user,
      usersById: {},
      status: detail.status,
      priority: detail.priority,
      isFinal: false,
      myPendingDecision: null,
      canEdit: false, canCancel: false, canRemind: false,
      showDeciderActions: false, showRequesterActions: false,
      isAccuse, isInfo,
      bottomTab: "history", setBottomTab: () => {},
      onApprove: () => {}, onRefuse: () => {}, onEdit: () => {},
      onCancel: () => {}, onRemind: () => {}, onGotoUser: () => {},
    };
  }, [detail, user]);

  if (!user || !current) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto"
      data-testid="avi-incoming-modal"
      style={{
        background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(4px)",
        padding: "40px 20px",
      }}
      // Bloque le click extérieur (rien à faire à part fermer via bouton).
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="relative rounded-xl w-full max-w-3xl"
        style={{
          background: "#141414",
          border: `2px solid ${meta?.color || "#EC4899"}`,
          boxShadow: `0 20px 60px rgba(0,0,0,0.7), 0 0 40px ${meta?.color || "#EC4899"}44`,
        }}
      >
        {/* ---------- Header : sous-type + priorité en haut à droite ------ */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: "#2A2A2A" }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle size={18} style={{ color: meta?.color }} />
            <div>
              <div
                className="text-[10px] uppercase tracking-widest font-bold"
                style={{ color: meta?.color }}
                data-testid="avi-incoming-subtype"
              >
                Nouvelle demande · {meta?.label}
              </div>
              <div className="text-sm font-semibold text-white/90 truncate mt-0.5">
                {current.subject || "—"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest"
              style={{ background: prioCol?.bg, border: `1px solid ${prioCol?.border}`, color: prioCol?.text }}
              data-testid="avi-incoming-priority-badge"
            >
              {PRIORITY_LABEL[current.priority] || current.priority}
            </span>
          </div>
        </div>

        {/* ---------- Corps ---------------------------------------------- */}
        <div className="max-h-[65vh] overflow-y-auto p-4" data-testid="avi-incoming-body">
          {loadingDetail || !bodyProps ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 size={16} className="animate-spin mr-2" /> Chargement de la fiche…
            </div>
          ) : (
            <AviDetailBody {...bodyProps} />
          )}
        </div>

        {/* ---------- Footer : actions ---------------------------------- */}
        <div
          className="flex items-center justify-between gap-3 p-4 border-t flex-wrap"
          style={{ borderColor: "#2A2A2A" }}
        >
          <div className="text-[11px] text-muted-foreground min-w-0">
            {remaining > 0 ? (
              <span data-testid="avi-incoming-remaining" className="inline-flex items-center gap-1">
                <Clock size={12} />
                Il reste <b className="text-white/80">{remaining}</b> autre{remaining > 1 ? "s" : ""} demande{remaining > 1 ? "s" : ""} en attente
              </span>
            ) : (
              <span className="text-muted-foreground/70">Dernière demande en attente.</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Ouvrir la fiche complète — spec iter160 · point 2 */}
            <button
              type="button"
              onClick={openFullDetail}
              disabled={submitting || loadingDetail}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
              style={{
                background: "transparent",
                border: "1.5px solid #4B5563",
                color: "#D1D5DB",
              }}
              data-testid="avi-incoming-open-full-btn"
              title="Ouvrir la fiche complète — la demande reviendra dans la file si vous ne décidez pas"
            >
              <ExternalLink size={13} /> Ouvrir la fiche
            </button>
            {/* Voir plus tard — vrai bouton encadré */}
            <button
              type="button"
              onClick={closeCurrent}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
              style={{
                background: "transparent",
                border: "1.5px solid #4B5563",
                color: "#D1D5DB",
              }}
              data-testid="avi-incoming-later-btn"
              title="Fermer sans décider — la demande reste en attente"
            >
              <X size={13} /> Voir plus tard
            </button>
            {/* Refuser (visible seulement pour validation) */}
            {meta?.negative && (
              <button
                type="button"
                onClick={() => submitDecision(meta.negativeDec)}
                disabled={submitting || loadingDetail}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                style={{ background: "#DC2626" }}
                data-testid="avi-incoming-negative-btn"
              >
                <XCircle size={13} /> {meta.negative}
              </button>
            )}
            {/* Décision positive */}
            <button
              type="button"
              onClick={() => submitDecision(meta.positiveDec)}
              disabled={submitting || loadingDetail}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
              style={{ background: meta?.color || "#22C55E" }}
              data-testid="avi-incoming-positive-btn"
            >
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              {meta?.positive}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
